import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EmailSignupForm from "@/components/EmailSignupForm";
import LatestEpisodeTicker from "@/components/LatestEpisodeTicker";
import { Headphones, ArrowUpRight, Mic } from "lucide-react";

const SPOTIFY_URL = "https://open.spotify.com/show/6IaiqWObnU6gkCVdu9u3YB";
const SPOTIFY_EMBED = "https://open.spotify.com/embed/show/6IaiqWObnU6gkCVdu9u3YB?utm_source=generator&theme=0";
const PODIUM_URL = "https://born-to-create.onpodium.com/";
const APPLE_URL = "https://podcasts.apple.com/podcast/born-to-create";
const AMAZON_URL = "https://music.amazon.com/podcasts/";
const PODCAST_LOGO = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/3yvkwofz_IMG_9614.jpg";
const EP41_ARTWORK = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/omj8pvlx_IMG_9613.jpg";

const platforms = [
  { name: "Spotify", href: SPOTIFY_URL },
  { name: "Apple Podcasts", href: APPLE_URL },
  { name: "Amazon Music", href: AMAZON_URL },
  { name: "All platforms", href: PODIUM_URL },
];

const themes = [
  "Creativity as a way of life, not a side project",
  "From passion to purpose, gently",
  "Breaking comfort zones without breaking yourself",
  "Intuition, authenticity, and listening inward",
  "Building a life around the work you love",
];

export default function Podcast() {
  useEffect(() => {
    document.title = "Born to Create · A podcast by Maria-Katharina Richters";
    const desc = "Born to Create. Conversations with artists who chose their work. Hosted by Maria-Katharina Richters. New episodes drop gently.";
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', 'Born to Create · A podcast');
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="podcast-page" className="min-h-screen bg-paper text-[#1A1A1A] flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section data-testid="podcast-hero" className="relative overflow-hidden border-b border-[#E5E0D8]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
            <div className="grid grid-cols-12 gap-8 items-start">
              <div className="col-span-12 md:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <Mic size={16} strokeWidth={1.4} className="text-[#1FA5B8]" />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                    Podcast · Live · 58 episodes
                  </span>
                </div>

                <div className="mb-8">
                  <LatestEpisodeTicker />
                </div>

                <h1
                  data-testid="podcast-headline"
                  className="font-display text-[#1A1A1A] tracking-tighter leading-[0.95] text-[clamp(3rem,8vw,6.5rem)] font-medium"
                >
                  Born to <span className="italic font-light">Create</span>.
                </h1>

                <p className="mt-6 font-display italic text-[#1FA5B8] text-[clamp(1.4rem,2.6vw,2rem)] leading-tight max-w-2xl">
                  Conversations with creatives who chose their art.
                </p>

                <div className="mt-8 max-w-2xl font-body text-[#1A1A1A] text-lg leading-[1.75] space-y-5">
                  <p>
                    A podcast for artists and aspiring creatives who want to
                    unleash their full potential and dare to build a life
                    around their work. Honest conversations with musicians,
                    painters, photographers, and makers about creativity,
                    freedom, and following the call.
                  </p>
                  <p className="text-[#5C5C5C]">
                    Hosted by me, Maria-Katharina Richters. New episodes drop
                    gently. No noise, just stories from people who chose to
                    listen to themselves and what arrived after.
                  </p>
                </div>

                <div className="mt-10 flex flex-wrap gap-3">
                  {platforms.map((p) => (
                    <a
                      key={p.name}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-2 px-5 h-11 font-body text-[12px] uppercase tracking-[0.22em] transition-colors ${
                        p.name === "Spotify"
                          ? "bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2]"
                          : "border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A]"
                      }`}
                    >
                      <Headphones size={14} strokeWidth={1.6} />
                      {p.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Cover art card */}
              <aside
                className="col-span-12 md:col-span-4 md:col-start-9 self-start"
                data-testid="podcast-cover-card"
              >
                <div className="relative border border-[#E5E0D8] bg-[#0A0A0A] aspect-square overflow-hidden shadow-[0_18px_40px_-20px_rgba(26,26,26,0.35)]">
                  <img
                    src={PODCAST_LOGO}
                    alt="Born to Create podcast cover"
                    className="w-full h-full object-cover block"
                  />
                </div>
                <ul className="mt-5 space-y-3 font-body text-[13px] text-[#1A1A1A]">
                  <li className="flex justify-between border-b border-[#E5E0D8] pb-2">
                    <span className="text-[#5C5C5C]">Host</span>
                    <span>Maria-Katharina</span>
                  </li>
                  <li className="flex justify-between border-b border-[#E5E0D8] pb-2">
                    <span className="text-[#5C5C5C]">Episodes</span>
                    <span>58 and counting</span>
                  </li>
                  <li className="flex justify-between border-b border-[#E5E0D8] pb-2">
                    <span className="text-[#5C5C5C]">Format</span>
                    <span>Artist interviews</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-[#5C5C5C]">Cost</span>
                    <span>Free, always</span>
                  </li>
                </ul>

                <Link
                  to="/work-with-me"
                  data-testid="podcast-to-wwm"
                  className="mt-5 inline-flex items-center justify-between w-full border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[11px] uppercase tracking-[0.22em] h-11 px-4 transition-colors"
                >
                  Work with Maria
                  <ArrowUpRight size={13} strokeWidth={1.6} />
                </Link>
              </aside>
            </div>
          </div>
        </section>

        {/* Featured episode */}
        <section data-testid="featured-episode" className="bg-[#FFFFFF] border-b border-[#E5E0D8]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-8 items-start">
              <div className="col-span-12 md:col-span-5">
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
                  Featured · Maria&apos;s pick
                </div>
                <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2rem,4vw,3.25rem)] font-medium">
                  Start <span className="italic font-light">here</span>.
                </h2>
                <p className="mt-5 font-body text-[#5C5C5C] leading-relaxed max-w-md">
                  If you don&apos;t know where to begin, begin with this one.
                  A favourite of mine, and one of the most loved by listeners.
                </p>
              </div>

              <article className="col-span-12 md:col-span-7 border border-[#E5E0D8] bg-[#F7F5F2] p-6 md:p-8">
                <div className="grid grid-cols-12 gap-6 md:gap-8 items-start">
                  {/* Episode artwork */}
                  <div className="col-span-12 sm:col-span-6">
                    <div className="border border-[#E5E0D8] bg-[#F7F5F2] aspect-square overflow-hidden shadow-[0_18px_40px_-20px_rgba(26,26,26,0.35)]">
                      <img
                        src={EP41_ARTWORK}
                        alt="Born to Create · Episode 41 · Ekaterina Popova"
                        className="w-full h-full object-contain block"
                      />
                    </div>
                  </div>

                  <div className="col-span-12 sm:col-span-6">
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                        Episode 41 · Artist Interview
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                        20 June 2023
                      </span>
                    </div>

                    <h3 className="mt-4 font-display text-[#1A1A1A] text-[26px] md:text-[30px] leading-[1.15] tracking-tight">
                      Ekaterina Popova
                    </h3>
                    <p className="mt-2 font-display italic text-[#5C5C5C] text-base md:text-lg leading-snug">
                      The power of tuning into your own intuition, and
                      nurturing each other as artists in business.
                    </p>

                    <p className="mt-4 font-body text-[#1A1A1A] text-[15px] leading-[1.7]">
                      Ekaterina Popova is an award-winning artist born in
                      Russia, known mostly for her original oil paintings of
                      interiors. A conversation about creative trust,
                      building a life around the work, and the quiet but
                      powerful art of holding each other up as artists.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <a
                        href={SPOTIFY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="featured-spotify"
                        className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
                      >
                        Listen on Spotify
                      </a>
                      <a
                        href={PODIUM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="featured-podium"
                        className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
                      >
                        Or another platform
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Email subscribe — placed high so it's impossible to miss */}
        <section data-testid="podcast-subscribe" className="bg-[#1A1A1A] text-[#F7F5F2] relative overflow-hidden border-b border-[#1A1A1A]">
          {/* Soft teal halo backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(40% 60% at 80% 20%, rgba(31,165,184,0.35) 0%, rgba(26,26,26,0) 70%), radial-gradient(45% 50% at 15% 85%, rgba(31,165,184,0.18) 0%, rgba(26,26,26,0) 70%)",
            }}
          />
          <div className="relative max-w-[920px] mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#1FA5B8]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#1FA5B8] font-body">
                Subscribe to Born to Create
              </span>
              <span className="h-px w-8 bg-[#1FA5B8]" />
            </div>

            <h2 className="font-display leading-[1] tracking-tight text-[clamp(2.5rem,5.4vw,4.5rem)] font-medium">
              New episodes,{" "}
              <span className="italic font-light">gently</span>, in your inbox.
            </h2>

            <p className="mt-6 font-body text-[#F7F5F2]/75 text-lg leading-[1.75] max-w-2xl mx-auto">
              When a new episode lands, I will tell you. Plus the occasional
              letter from the studio. No noise, no funnels. Reply to any
              email and you reach me directly.
            </p>

            <div className="mt-10 max-w-xl mx-auto text-left">
              <EmailSignupForm
                source="born_to_create"
                testIdPrefix="podcast"
                dark
                redirectTo={null}
                successDescription="You're on the list. Next episode in your inbox."
              />
              <p className="mt-5 font-body text-xs text-[#F7F5F2]/50 leading-relaxed text-center">
                Free. Quiet. Easy to leave any time.
              </p>
            </div>
          </div>
        </section>

        {/* Listen / Browse */}
        <section data-testid="podcast-listen" className="bg-[#FFFFFF] border-b border-[#E5E0D8]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-8 items-start">
              <div className="col-span-12 md:col-span-5">
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
                  The library
                </div>
                <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2rem,4vw,3.25rem)] font-medium">
                  58 conversations.{" "}
                  <span className="italic font-light">Start</span> wherever
                  pulls you.
                </h2>
                <p className="mt-5 font-body text-[#5C5C5C] leading-relaxed max-w-md">
                  Each episode stands alone. Some are quiet, some are
                  electric. Browse the full library on the platform of your
                  choice and choose what calls to you today.
                </p>
              </div>

              <div className="col-span-12 md:col-span-6 md:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {platforms.map((p) => (
                  <a
                    key={p.name}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`library-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group border border-[#E5E0D8] bg-[#F7F5F2] p-6 flex flex-col justify-between hover:border-[#1FA5B8] transition-colors"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                        Listen on
                      </span>
                      <Headphones size={16} strokeWidth={1.4} className="text-[#5C5C5C] group-hover:text-[#1FA5B8] transition-colors" />
                    </div>
                    <div className="mt-6 font-display text-[#1A1A1A] text-[22px] md:text-[24px] leading-none tracking-tight">
                      {p.name}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1 font-body text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A] group-hover:text-[#1FA5B8] transition-colors">
                      Open library
                      <ArrowUpRight size={12} strokeWidth={1.6} />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Themes */}
        <section data-testid="podcast-themes" className="bg-[#F7F5F2] border-b border-[#E5E0D8]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-12 gap-8 mb-12 items-end">
              <div className="col-span-12 md:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
                  What you will hear
                </div>
                <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2rem,4.4vw,3.5rem)] font-medium">
                  Themes the show keeps{" "}
                  <span className="italic font-light">returning</span> to.
                </h2>
              </div>
            </div>
            <ul className="grid grid-cols-12 gap-x-8 gap-y-6">
              {themes.map((t, i) => (
                <li
                  key={i}
                  data-testid={`theme-${i + 1}`}
                  className="col-span-12 md:col-span-6 border-t border-[#1A1A1A] pt-5"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-[20px] leading-none tracking-tight text-[#1FA5B8]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body">
                      Recurring thread
                    </span>
                  </div>
                  <p className="mt-4 font-display text-[#1A1A1A] text-[22px] leading-[1.35]">
                    {t}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
